import React from 'react';
import { Trash2, Check } from 'lucide-react';
import { CheckoutAddon } from '../../types';
import { CURRENCIES } from '../../constants';

interface AddonItemRowProps {
  addon: CheckoutAddon;
  onEdit: (addon: CheckoutAddon) => void;
  onToggle: (id: string) => void;
  onDelete: (id: string) => void;
}

const AddonItemRow: React.FC<AddonItemRowProps> = ({ addon, onEdit, onToggle, onDelete }) => {
  const features = addon.features || [];
  const symbol = CURRENCIES.find(c => c.code === addon.currency)?.symbol || '₹';

  return (
    <div className="p-4 bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
      <div className="flex-1 min-w-0 space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bold text-sm text-slate-900 dark:text-white">{addon.name}</span>
          <code className="text-[10px] font-mono text-slate-400 bg-white dark:bg-slate-800 px-2 py-0.5 rounded border border-slate-200 dark:border-slate-700">
            {addon.id}
          </code>
          <span className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 rounded-full border border-emerald-200 dark:border-emerald-800">
            {symbol}{addon.price} / mo
          </span>
        </div>

        {addon.description && (
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {addon.description}
          </p>
        )}

        {/* Multi-features list */}
        {features.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {features.map((feat, idx) => (
              <span
                key={idx}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-[11px] font-medium text-slate-700 dark:text-slate-300"
              >
                <Check className="w-3 h-3 text-emerald-500 shrink-0" />
                {feat}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 self-end sm:self-center">
        <button
          type="button"
          onClick={() => onEdit(addon)}
          className="px-2.5 py-1 text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/50 rounded-lg transition-colors"
        >
          Edit
        </button>
        <button
          type="button"
          onClick={() => onToggle(addon.id)}
          className={`w-9 h-5 rounded-full relative p-0.5 transition-colors ${addon.isEnabled ? 'bg-indigo-600' : 'bg-slate-300 dark:bg-slate-700'}`}
          title={addon.isEnabled ? 'Active' : 'Disabled'}
        >
          <div className={`w-4 h-4 bg-white rounded-full shadow-xs transition-transform ${addon.isEnabled ? 'translate-x-4' : 'translate-x-0'}`} />
        </button>
        <button
          type="button"
          onClick={() => onDelete(addon.id)}
          className="text-slate-400 hover:text-red-500 p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/50 transition-colors"
          title="Delete Add-on"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default AddonItemRow;
